import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageSquare, Phone, Truck, Wrench } from "lucide-react";
import api from "../lib/api";
import { useSite } from "../lib/site";
import { enquireHref, pageHref } from "../lib/format";
import SmartImage from "../components/SmartImage";
import Breadcrumbs from "../components/Breadcrumbs";
import ItemCard from "../components/ItemCard";
import Lightbox from "../components/Lightbox";
import { ErrorState, PageLoader } from "../components/States";

export default function ItemDetail() {
  const { id } = useParams();
  const { site } = useSite();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    setData(null);
    setError(false);
    window.scrollTo({ top: 0 });
    api
      .get(`/catalogue/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="container-nq section-nq">
        <ErrorState title="Product not found" message="This item may have been moved or removed." />
        <div className="flex justify-center">
          <Link to="/" className="btn-gold">
            Back home
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <PageLoader />;

  const { item, related = [], page_title: pageTitle, breadcrumbs = [] } = data;
  const images = [item.image, ...(item.gallery || [])].filter(Boolean);
  const phone = site?.contact?.phone || "";

  return (
    <div data-testid="product-detail">
      <div
        className="border-b"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}
      >
        <div className="container-nq py-5">
          <Breadcrumbs items={[...breadcrumbs, { slug: "", label: item.name }]} />
        </div>
      </div>

      <div className="container-nq section-nq">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7" data-testid="product-gallery">
            <button
              type="button"
              onClick={() => images.length && setLb(0)}
              className="card-nq block w-full cursor-zoom-in"
            >
              <SmartImage
                src={item.image}
                alt={item.image_alt || item.name}
                ratio="aspect-[4/3]"
                w={1200}
                fit="contain"
                fallbackLabel={item.brand || item.name}
                loading="eager"
              />
            </button>

            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.map((im, i) => (
                  <button key={i} type="button" onClick={() => setLb(i)} className="card-nq">
                    <SmartImage src={im} alt="" ratio="aspect-square" fit="contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28">
              {item.brand && <p className="eyebrow">{item.brand}</p>}
              <h1 className="mt-2 text-2xl font-bold leading-tight md:text-3xl">{item.name}</h1>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                {item.price ? (
                  <span className="font-mono text-2xl font-bold gold-text">{item.price}</span>
                ) : (
                  <span
                    className="rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider gold-text"
                    style={{ borderColor: "rgba(198,162,74,0.4)" }}
                  >
                    Enquiry only
                  </span>
                )}
                {pageTitle && (
                  <Link
                    to={pageHref(item.page_slug)}
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{ background: "rgba(31,107,58,0.28)" }}
                  >
                    {pageTitle}
                  </Link>
                )}
              </div>

              {item.description && (
                <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-[color:var(--nq-stone)]">
                  {item.description}
                </p>
              )}

              <div className="mt-6 space-y-2">
                <Link
                  to={enquireHref({
                    type: "item",
                    id: item.id,
                    title: item.name,
                    image: item.image,
                    price: item.price,
                    category: pageTitle,
                    brand: item.brand,
                  })}
                  className="btn-gold w-full px-6 py-3"
                  data-testid="product-enquire-button"
                >
                  <MessageSquare className="h-4 w-4" /> Enquire about this product
                </Link>
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                    className="btn-outline-nq w-full px-6 py-3"
                  >
                    <Phone className="h-4 w-4" /> Call {phone}
                  </a>
                )}
              </div>

              <ul
                className="mt-6 space-y-2.5 border-t pt-5 text-xs text-[color:var(--nq-stone)]"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              >
                <li className="flex items-center gap-2.5">
                  <Wrench className="h-4 w-4 gold-text" /> Professional fitting available in
                  Townsville
                </li>
                <li className="flex items-center gap-2.5">
                  <Truck className="h-4 w-4 gold-text" /> Australia-wide shipping on stocked items
                </li>
              </ul>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <p className="eyebrow">More from {pageTitle}</p>
            <h2 className="mt-2 text-2xl font-bold">Related products</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <ItemCard key={r.id} item={r} pageTitle={pageTitle} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Lightbox images={images} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
